import { Contract, OfferMessage } from '@atomicfinance/bitcoin-dlc-provider';
import {
  Button,
  Flex,
  FormLabel,
  Heading,
  Text,
  Textarea,
} from '@chakra-ui/core';
import * as bip39 from 'bip39';
import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import config from '../../config/config';
import { usePersistBet } from '../../hooks/usePersistBet';
import { Bitcoin, createClient } from '../../services/chainClient';
import TowerService, { getTowerService } from '../../services/towerService';
import { getTwitterInfoById } from '../../services/twitter';
import { useAppDispatch } from '../../store';
import { setMnemonic } from '../../store/wallet/walletSlice';

const RestoreSeed = ({ setStep }) => {
  const [mnemonic, _setMnemonic] = useState('');

  // handle encrypt, then grab info from tower, then store that as well

  const [error, setError] = useState(false);

  const bitcoinRef = useRef<Bitcoin | null>(null);
  const tower = useRef<TowerService | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const persistBet = usePersistBet(mnemonic);
  const dispatch = useAppDispatch();

  let { search } = useLocation();

  const query = new URLSearchParams(search);

  const validateAndRestore = async () => {
    if (!validateMnemonic()) return;

    setIsImporting(true);
    dispatch(setMnemonic(mnemonic));

    const bitcoin = createClient(mnemonic);
    bitcoinRef.current = bitcoin;

    const addresses = await bitcoinRef.current?.client.getMethod(
      'getAddresses'
    )(0, 2, false);
    const pubkey = addresses[1].publicKey.toString('hex');

    await initializeTower();

    const contractStages = {};
    await localImport(contractStages, pubkey);
    await remoteImport(contractStages, pubkey);

    const contracts = await bitcoin.client.finance.dlc.exportContracts();

    for (let i = 0; i < contracts.length; i++) {
      const contract = contracts[i];

      const { isLocalParty, localCollateral, remoteCollateral } = contract;

      let asset, publishDate, eventType;
      if (
        contract.oracleInfo.rValue ===
        '031f9fefc7c0be9b3cd14671590892ebf01c440034f1b193229bca3275a4217d89'
      ) {
        asset = 'election';
        publishDate = '2020-10-19T17:00:00Z';
        eventType = 'election';
      } else {
        const rValueResponse = await fetch(
          `${config.oracle.url}/asset/rvalue/${contract.oracleInfo.rValue}`
        );
        let rValueData = await rValueResponse.json();

        asset = rValueData.asset;
        publishDate = rValueData.publishDate;
        eventType = rValueData.eventType;
      }

      // const eventValue = eventType.split('above(')[1].split(')')[0]

      const localBetSize = localCollateral.GetSatoshiAmount();
      const remoteBetSize = remoteCollateral.GetSatoshiAmount();
      const betSize = localBetSize + remoteBetSize;

      let betAmount = 0;
      let betAmountCounter = 0;
      if (isLocalParty) {
        betAmount = localBetSize;
        betAmountCounter = remoteBetSize;
      } else {
        betAmount = remoteBetSize;
        betAmountCounter = localBetSize;
      }

      const betDetails = {
        event: asset,
        eventType,
        prediction: contract.outcomes[isLocalParty ? 0 : 1].message,
        resolutionDate: publishDate,
        betSize,
        betAmount,
        betAmountCounter,
      };

      await persistBet(contract.id, betDetails, contractStages[contract.id]);
    }

    try {
      const response = await tower.current?.getUserInfo(pubkey);
      if (response) {
        const {
          data: { twitterId },
        } = response;

        if (twitterId) {
          const twitterInfo = await getTwitterInfoById(twitterId);
          localStorage.setItem('twitterInfo', JSON.stringify(twitterInfo));
        }
      }
    } catch (e) {}

    setIsImporting(false);
    setStep(2);
  };

  const localImport = async (contractStages, pubkey) => {
    const offersResponse = await tower.current?.getOffers(pubkey);
    if (offersResponse) {
      const { data: offers } = offersResponse;

      for (let i = 0; i < offers.length; i++) {
        const offer = offers[i];

        const contract = Contract.fromJSON(JSON.parse(offer.offerData));
        const finalAddress = contract.localPartyInputs.finalAddress;
        const addresses = await bitcoinRef.current?.client.getMethod(
          'getAddresses'
        )(0, 50, false);

        let startingIndex: number = 0;
        addresses.forEach((address, i) => {
          if (address.address === finalAddress) {
            startingIndex = parseInt(i);
          }
        });

        const offerMessage = OfferMessage.fromJSON(JSON.parse(offer.offerData));
        const acceptMessage = await tower.current?.getAccept(
          offer.contractUUID,
          startingIndex
        );
        const signMessage = await tower.current?.getRemoteSign(
          offer.contractUUID,
          acceptMessage?.remotePartyInputs.fundPublicKey,
          startingIndex
        );

        if (signMessage) {
          await bitcoinRef.current?.client.finance.dlc.importContractFromAcceptAndSignMessage(
            offerMessage,
            acceptMessage,
            signMessage,
            startingIndex
          );

          const updatedContract = bitcoinRef.current?.client.finance.dlc.exportContract(
            offer.contractUUID
          );

          try {
            await bitcoinRef.current?.client.getMethod('getTransaction')(
              updatedContract.fundTxId
            );

            contractStages[offer.contractUUID] = 'ACTIVE';
          } catch (e) {
            contractStages[offer.contractUUID] = 'SIGN';
          }
        } else {
          await bitcoinRef.current?.client.finance.dlc.importContractFromOfferMessage(
            offerMessage,
            startingIndex
          );
          contractStages[offer.contractUUID] = 'OFFER';
        }
      }
    }
  };

  const remoteImport = async (contractStages, pubkey) => {
    const remoteMessages = await tower.current?.getRemoteMessages(pubkey);

    if (remoteMessages) {
      for (let i = 0; i < remoteMessages.length; i++) {
        const {
          offerMessage,
          acceptMessage,
          signMessage,
          startingIndex,
        } = remoteMessages[i];

        await bitcoinRef.current?.client.finance.dlc.importContractFromAcceptMessage(
          offerMessage,
          acceptMessage,
          startingIndex
        );

        const contract = bitcoinRef.current?.client.finance.dlc.exportContract(
          offerMessage.contractId
        );

        try {
          await bitcoinRef.current?.client.getMethod('getTransaction')(
            contract.fundTxId
          );

          try {
            await bitcoinRef.current?.client.finance.dlc.finalizeContract(
              signMessage
            );
          } catch (e) {}
          contractStages[offerMessage.contractId] = 'ACTIVE';
        } catch (e) {
          contractStages[offerMessage.contractId] = 'ACCEPT';
        }
      }
    }
  };

  const validateMnemonic = () => {
    const words = mnemonic.trim().toLowerCase().split(' ');
    let errored = false;

    if (!(words.length === 12 || words.length === 18 || words.length === 24)) {
      setError(true);
      errored = true;
    }

    words.forEach((word) => {
      if (!bip39.wordlists.english.includes(word)) {
        setError(true);
        errored = true;
      }
    });

    return !errored;
  };

  const initializeTower = async () => {
    if (tower.current) return;
    if (!bitcoinRef.current) throw new Error('Bitcoin client not initialized');

    const bitcoin = bitcoinRef.current;
    const towerInstance = getTowerService(bitcoin);
    await towerInstance.initialize();

    tower.current = towerInstance;
  };

  return (
    <>
      <Heading size="md">Sign In</Heading>
      <FormLabel fontSize="sm" mt={4}>
        Enter your secret 12 word phrase to restore your bets.
      </FormLabel>
      <Textarea
        isInvalid={error}
        placeholder="Enter here"
        onChange={(e) => {
          _setMnemonic(
            e.target.value.trim().toLowerCase() +
              (query.get('hotfix') ? '\n' : '')
          );
        }}
      />

      <Text>
        <b>Note:</b> You will be asked to create a new password after importing
        your seed.
      </Text>

      <Flex mt={4}>
        <Button
          ml="auto"
          colorScheme="blue"
          isLoading={isImporting}
          loadingText="Restoring bets..."
          onClick={() => validateAndRestore()}
        >
          Next Step
        </Button>
      </Flex>
    </>
  );
};

export default RestoreSeed;
